import { ReactNode } from 'react';
import { BaseEditor, BaseRange, BaseSelection, BaseElement, BaseText, Descendant } from 'slate';
import { ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';

// Extend Slate's types to include our custom properties
declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// Slate Editor Types
export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor;

// Element Types
export type CustomElement = 
  | ParagraphElement
  | HeadingElement
  | ListItemElement
  | BulletedListElement
  | NumberedListElement
  | ImageElement
  | TableElement
  | TableRowElement
  | TableCellElement
  | CodeBlockElement
  | HorizontalRuleElement
  | ChecklistElement;

// Text Types
export type CustomText = BaseText & {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontFamily?: string;
  superscript?: boolean;
  subscript?: boolean;
  link?: string;
};

// Element Definitions
export interface ParagraphElement extends BaseElement {
  type: 'paragraph';
  align?: 'left' | 'center' | 'right' | 'justify';
  indent?: number;
  children: CustomText[];
}

export interface HeadingElement extends BaseElement {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  align?: 'left' | 'center' | 'right' | 'justify';
  children: CustomText[];
}

export interface ListItemElement extends BaseElement {
  type: 'list-item';
  children: CustomText[];
}

export interface BulletedListElement extends BaseElement {
  type: 'bulleted-list';
  children: ListItemElement[];
}

export interface NumberedListElement extends BaseElement {
  type: 'numbered-list';
  children: ListItemElement[];
}

export interface ImageElement extends BaseElement {
  type: 'image';
  url: string;
  alt?: string;
  caption?: string;
  align?: 'left' | 'center' | 'right';
  width?: number;
  height?: number;
  children: CustomText[];
}

export interface TableElement extends BaseElement {
  type: 'table';
  rows: number;
  cols: number;
  children: TableRowElement[];
}

export interface TableRowElement extends BaseElement {
  type: 'table-row';
  children: TableCellElement[];
}

export interface TableCellElement extends BaseElement {
  type: 'table-cell';
  header?: boolean;
  align?: 'left' | 'center' | 'right';
  children: CustomText[];
}

export interface CodeBlockElement extends BaseElement {
  type: 'code-block';
  language?: string;
  children: CustomText[];
}

export interface HorizontalRuleElement extends BaseElement {
  type: 'horizontal-rule';
  style?: 'solid' | 'dashed' | 'dotted';
  thickness?: number;
  color?: string;
  children: CustomText[];
}

export interface ChecklistElement extends BaseElement {
  type: 'checklist';
  checked: boolean;
  children: CustomText[];
}

// Plugin Types
export interface Plugin {
  name: string;
  renderElement?: (props: any) => ReactNode | undefined;
  renderLeaf?: (props: any) => ReactNode | undefined;
  onKeyDown?: (event: KeyboardEvent, editor: CustomEditor) => boolean;
  toolbarButton?: () => ReactNode;
  commands?: Record<string, (editor: CustomEditor, ...args: any[]) => void>;
}

// Editor Props
export interface MJEditorProps {
  value?: CustomElement[];
  onChange?: (value: CustomElement[]) => void;
  placeholder?: string;
  readOnly?: boolean;
  plugins?: Plugin[];
  theme?: 'light' | 'dark';
  className?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  autoSave?: boolean;
  autoSaveInterval?: number;
  onSave?: (value: CustomElement[]) => void;
  toolbarConfig?: ToolbarConfig;
  maxLength?: number;
  minHeight?: string;
  maxHeight?: string;
}

// Toolbar Configuration
export interface ToolbarConfig {
  showTextFormatting?: boolean;
  showFontControls?: boolean;
  showTextStyling?: boolean;
  showTextAlignment?: boolean;
  showIndentation?: boolean;
  showLists?: boolean;
  showTextPositioning?: boolean;
  showInsertOptions?: boolean;
  showAdvancedFeatures?: boolean;
  showUndoRedo?: boolean;
  disabledTools?: string[];
  customButtons?: ReactNode[];
}

// Color Options
export interface ColorOption {
  name: string;
  value: string;
  label: string;
}

// Font Options
export interface FontOption {
  name: string;
  value: string;
  label: string;
}

// Size Options
export interface SizeOption {
  name: string;
  value: number;
  label: string;
}

// Export Types
export interface ExportOptions {
  format: 'html' | 'markdown' | 'plain-text' | 'json';
  includeStyles?: boolean;
  preserveFormatting?: boolean;
}

// History Types
export interface HistoryState {
  undos: any[];
  redos: any[];
}

// Selection Types
export type CustomSelection = BaseSelection;
export type CustomRange = BaseRange;

// Utility Types
export type ElementType = CustomElement['type'];
export type MarkType = keyof Omit<CustomText, 'text'>;

// Event Types
export interface EditorEvent {
  type: string;
  data?: any;
  timestamp: number;
}

// Configuration Types
export interface EditorConfig {
  plugins: Plugin[];
  theme: 'light' | 'dark';
  toolbar: ToolbarConfig;
  autoSave: boolean;
  autoSaveInterval: number;
  maxLength?: number;
  placeholder?: string;
}

// Default Values
export const DEFAULT_FONTS: FontOption[] = [
  { name: 'Lato Regular', value: 'Lato, sans-serif', label: 'Lato Regular' },
  { name: 'Arial', value: 'Arial, sans-serif', label: 'Arial' },
  { name: 'Times New Roman', value: 'Times New Roman, serif', label: 'Times New Roman' },
  { name: 'Helvetica', value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { name: 'Georgia', value: 'Georgia, serif', label: 'Georgia' },
  { name: 'Verdana', value: 'Verdana, sans-serif', label: 'Verdana' },
  { name: 'Courier New', value: 'Courier New, monospace', label: 'Courier New' }
];

export const DEFAULT_SIZES: SizeOption[] = [
  { name: '8pt', value: 8, label: '8' },
  { name: '9pt', value: 9, label: '9' },
  { name: '10pt', value: 10, label: '10' },
  { name: '11pt', value: 11, label: '11' },
  { name: '12pt', value: 12, label: '12' },
  { name: '14pt', value: 14, label: '14' },
  { name: '16pt', value: 16, label: '16' },
  { name: '18pt', value: 18, label: '18' },
  { name: '20pt', value: 20, label: '20' },
  { name: '22pt', value: 22, label: '22' },
  { name: '24pt', value: 24, label: '24' },
  { name: '26pt', value: 26, label: '26' },
  { name: '28pt', value: 28, label: '28' },
  { name: '36pt', value: 36, label: '36' },
  { name: '48pt', value: 48, label: '48' },
  { name: '72pt', value: 72, label: '72' }
];

export const DEFAULT_COLORS: ColorOption[] = [
  { name: 'Black', value: '#000000', label: 'Black' },
  { name: 'Red', value: '#FF0000', label: 'Red' },
  { name: 'Green', value: '#00FF00', label: 'Green' },
  { name: 'Blue', value: '#0000FF', label: 'Blue' },
  { name: 'Yellow', value: '#FFFF00', label: 'Yellow' },
  { name: 'Orange', value: '#FFA500', label: 'Orange' },
  { name: 'Purple', value: '#800080', label: 'Purple' },
  { name: 'Pink', value: '#FFC0CB', label: 'Pink' },
  { name: 'Brown', value: '#A52A2A', label: 'Brown' },
  { name: 'Gray', value: '#808080', label: 'Gray' }
];

export const DEFAULT_HIGHLIGHT_COLORS: ColorOption[] = [
  { name: 'Yellow', value: '#FFFF00', label: 'Yellow' },
  { name: 'Green', value: '#90EE90', label: 'Green' },
  { name: 'Blue', value: '#87CEEB', label: 'Blue' },
  { name: 'Pink', value: '#FFB6C1', label: 'Pink' },
  { name: 'Orange', value: '#FFD700', label: 'Orange' },
  { name: 'Purple', value: '#DDA0DD', label: 'Purple' },
  { name: 'Red', value: '#FFB6C1', label: 'Red' },
  { name: 'Gray', value: '#D3D3D3', label: 'Gray' }
]; 